import type { PayloadAction } from "@reduxjs/toolkit";

import { createSlice } from "@reduxjs/toolkit";

import {
	type ConsolidationCustomerGrouping,
	type ConsolidationDirection,
	type ConsolidationLocationGrouping,
	ConsolidationPreviewRollupLevel
} from "~/gql/graphqlBase.generated";

export interface ConsolidationRuleOverrides {
	customerGrouping?: ConsolidationCustomerGrouping;
	direction?: ConsolidationDirection;
	locationGrouping?: ConsolidationLocationGrouping;
	lookBackWeeks?: number;
	lookForwardWeeks?: number;
	minimumLotQuantity?: number;
	roundingUnit?: number | null;
}

export interface StagedConsolidationGroup {
	autoQuantity: number;
	customerGroupId?: string;
	label: string;
	newQuantity: number;
	productionCatalogId: string;
	productionCategoryId: string;
	sourceProductionLotIds: string[];
	weekReady: string;
}

export interface ConsolidationState {
	open: boolean;
	rangeWeeks: number;
	rollupLevel: ConsolidationPreviewRollupLevel;
	ruleOverrides: ConsolidationRuleOverrides;
	stagedGroups: Record<string, StagedConsolidationGroup>;
}

const initialState: ConsolidationState = {
	open: false,
	rangeWeeks: 8,
	rollupLevel: ConsolidationPreviewRollupLevel.Genus,
	ruleOverrides: {},
	stagedGroups: {}
};

const consolidationSlice = createSlice({
	initialState,
	name: "consolidation",
	reducers: {
		clearStagedGroups: (state) => {
			state.stagedGroups = {};
		},
		closeConsolidation: () => initialState,
		openConsolidation: () => ({ ...initialState, open: true }),
		resetConsolidation: (state) => ({ ...initialState, open: state.open }),
		setRangeWeeks: (state, action: PayloadAction<number>) => {
			state.rangeWeeks = action.payload;
			state.stagedGroups = {};
		},
		setRollupLevel: (state, action: PayloadAction<ConsolidationPreviewRollupLevel>) => {
			state.rollupLevel = action.payload;
			state.stagedGroups = {};
		},
		setStagedGroupCustomerGroup: (state, action: PayloadAction<{ customerGroupId: string; key: string }>) => {
			const group = state.stagedGroups[action.payload.key];
			if (!group) return;
			if (action.payload.customerGroupId) group.customerGroupId = action.payload.customerGroupId;
			else delete group.customerGroupId;
		},
		setStagedGroupQuantity: (state, action: PayloadAction<{ key: string; newQuantity: number }>) => {
			const group = state.stagedGroups[action.payload.key];
			if (group) group.newQuantity = action.payload.newQuantity;
		},
		stageGroup: (state, action: PayloadAction<{ group: StagedConsolidationGroup; key: string }>) => {
			state.stagedGroups[action.payload.key] = action.payload.group;
		},
		unstageGroup: (state, action: PayloadAction<string>) => {
			delete state.stagedGroups[action.payload];
		},
		updateRuleOverrides: (state, action: PayloadAction<ConsolidationRuleOverrides>) => {
			state.ruleOverrides = { ...state.ruleOverrides, ...action.payload };
		}
	}
});

export const {
	clearStagedGroups,
	closeConsolidation,
	openConsolidation,
	resetConsolidation,
	setRangeWeeks,
	setRollupLevel,
	setStagedGroupCustomerGroup,
	setStagedGroupQuantity,
	stageGroup,
	unstageGroup,
	updateRuleOverrides
} = consolidationSlice.actions;

export default consolidationSlice.reducer;
